import { popup } from '@tma.js/sdk-react'
import { type RefObject, useEffect, useState } from 'react'
import {
  type ApiSupplier,
  type ApiSupplierInput,
  createSupplier,
  deleteSupplier,
  updateSupplier,
} from '@/fetcher'

type SupplierFormPageProps = {
  teamId: string
  supplier: ApiSupplier | null
  submitRef: RefObject<(() => void) | null>
  onBusyChange: (isBusy: boolean) => void
  onSaved: (supplier: ApiSupplier) => void
  onDeleted: (supplierId: string) => void
}

type FormState = {
  name: string
  description: string
  supplierName: string
  supplierAddress: string
  supplierAccount: string
  supplierBank: string
  supplierBankAddress: string
  supplierBankBranch: string
  supplierBankCode: string
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  supplierName: '',
  supplierAddress: '',
  supplierAccount: '',
  supplierBank: '',
  supplierBankAddress: '',
  supplierBankBranch: '',
  supplierBankCode: '',
}

const fromSupplier = (supplier: ApiSupplier | null): FormState => {
  if (!supplier) return { ...EMPTY_FORM }
  return {
    name: supplier.name,
    description: supplier.description ?? '',
    supplierName: supplier.values.supplierName,
    supplierAddress: supplier.values.supplierAddress,
    supplierAccount: supplier.values.supplierAccount,
    supplierBank: supplier.values.supplierBank,
    supplierBankAddress: supplier.values.supplierBankAddress,
    supplierBankBranch: supplier.values.supplierBankBranch,
    supplierBankCode: supplier.values.supplierBankCode,
  }
}

const toInput = (form: FormState): ApiSupplierInput => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
  supplierName: form.supplierName.trim(),
  supplierAddress: form.supplierAddress.trim(),
  supplierAccount: form.supplierAccount.trim(),
  supplierBank: form.supplierBank.trim(),
  supplierBankAddress: form.supplierBankAddress.trim(),
  supplierBankBranch: form.supplierBankBranch.trim(),
  supplierBankCode: form.supplierBankCode.trim(),
})

type FieldConfig = {
  key: keyof FormState
  label: string
  placeholder?: string
  multiline?: boolean
}

const FIELDS: readonly FieldConfig[] = [
  { key: 'name', label: 'Название', placeholder: 'ACME' },
  {
    key: 'description',
    label: 'Описание',
    placeholder: 'Необязательно',
    multiline: true,
  },
  {
    key: 'supplierName',
    label: 'Наименование поставщика',
    placeholder: 'ACME Co., Ltd.',
  },
  {
    key: 'supplierAddress',
    label: 'Адрес поставщика',
    placeholder: '1-2-3 Shibuya, Tokyo',
    multiline: true,
  },
  {
    key: 'supplierAccount',
    label: 'Счет поставщика',
    placeholder: '1234567890123456',
  },
  {
    key: 'supplierBank',
    label: 'Банк поставщика',
    placeholder: 'MUFG Bank, Ltd.',
  },
  {
    key: 'supplierBankAddress',
    label: 'Адрес банка поставщика',
    placeholder: 'Tokyo, Japan',
    multiline: true,
  },
  {
    key: 'supplierBankBranch',
    label: 'Филиал банка поставщика',
    placeholder: 'Shibuya Branch',
  },
  {
    key: 'supplierBankCode',
    label: 'Код банка поставщика',
    placeholder: '0005',
  },
]

export const SupplierFormPage = ({
  teamId,
  supplier,
  submitRef,
  onBusyChange,
  onSaved,
  onDeleted,
}: SupplierFormPageProps) => {
  const isEdit = supplier !== null
  const [form, setForm] = useState<FormState>(() => fromSupplier(supplier))
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    setForm(fromSupplier(supplier))
    setErrorMessage(null)
  }, [supplier])

  useEffect(() => {
    onBusyChange(isSaving || isDeleting)
  }, [isSaving, isDeleting, onBusyChange])

  const updateField = (key: keyof FormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const handleSubmit = async () => {
    const input = toInput(form)

    if (!input.name) {
      setErrorMessage('Укажите название поставщика')
      return
    }

    setIsSaving(true)
    try {
      const saved =
        isEdit && supplier
          ? await updateSupplier(teamId, supplier.id, input)
          : await createSupplier(teamId, input)
      onSaved(saved)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Не удалось сохранить поставщика',
      )
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    submitRef.current = handleSubmit
    return () => {
      submitRef.current = null
    }
  })

  const handleDelete = () => {
    if (!supplier) return

    const result = popup.show.ifAvailable({
      title: 'Удаление поставщика',
      message: `Удалить поставщика «${supplier.name}»? Действие нельзя отменить.`,
      buttons: [
        { id: 'delete', type: 'destructive', text: 'Удалить' },
        { id: 'cancel', type: 'cancel' },
      ],
    })

    if (!result.ok) return

    result.data.then((buttonId?: string) => {
      if (buttonId !== 'delete') return

      setIsDeleting(true)
      deleteSupplier(teamId, supplier.id)
        .then(() => {
          onDeleted(supplier.id)
        })
        .catch((error) => {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Не удалось удалить поставщика',
          )
        })
        .finally(() => {
          setIsDeleting(false)
        })
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--app-text)]">
          {isEdit ? 'Редактирование поставщика' : 'Новый поставщик'}
        </h1>
      </div>

      <div className="space-y-3">
        {FIELDS.map((field) => (
          <div key={field.key} className="space-y-2">
            <label
              className="block text-sm font-medium text-[var(--app-text)]"
              htmlFor={`supplier-${field.key}`}
            >
              {field.label}
            </label>
            {field.multiline ? (
              <textarea
                id={`supplier-${field.key}`}
                className="w-full min-h-20 rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={(event) => updateField(field.key, event.target.value)}
              />
            ) : (
              <input
                id={`supplier-${field.key}`}
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                placeholder={field.placeholder}
                value={form[field.key]}
                onChange={(event) => updateField(field.key, event.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      {isEdit ? (
        <button
          type="button"
          disabled={isSaving || isDeleting}
          onClick={handleDelete}
          className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-[var(--app-danger-soft)] px-5 py-3 text-sm font-semibold text-[var(--app-danger)] transition hover:opacity-90 disabled:opacity-50"
        >
          {isDeleting ? 'Удаление...' : 'Удалить поставщика'}
        </button>
      ) : null}

      {errorMessage ? (
        <div className="rounded-[18px] border border-[color:var(--app-danger)]/20 bg-[var(--app-danger-soft)] px-4 py-3 text-sm leading-6 text-[var(--app-text)]">
          {errorMessage}
        </div>
      ) : null}
    </div>
  )
}
